import React from 'react';

import {SearchContext} from 'search/SearchConstants';
import {Operator} from 'search/SearchConstants';
import ItemSearchInput, {Item, ItemSearchInputProps} from 'ui-library/attach-dropdown/ItemSearchInput';
import * as Keyboard from 'util/Keyboard';
import pickDefined from 'util/pickDefined';
import {WithDefaultPropsOptional} from 'util/Types';

import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './FilterFormConstants';
import FilterFormSkeletonNodeLayout from './FilterFormSkeletonNodeLayout';
import OperatorDropdown from './OperatorDropdown';
import PrimaryFilterFormSkeleton from './PrimaryFilterFormSkeleton';
import SecondaryFilterFormSkeleton from './SecondaryFilterFormSkeleton';

// Props for components that use ItemSearchInput, e.g. StorableSearchInput
type ItemSearchInputPropsWithDefaultsOptional<TItem> = WithDefaultPropsOptional<
  ItemSearchInputProps<TItem>,
  typeof ItemSearchInput.defaultProps
>;
export type ItemSearchInputParentProps<TItem> = Omit<
  ItemSearchInputPropsWithDefaultsOptional<TItem>,
  // We don't need to provide these props because they're specified by the component that uses ItemSearchInput
  'itemKey' | 'searchURL'
>;

interface ItemSearchInputFilterFormProps<TItem extends Item> extends BaseFilterFormProps {
  filterStyle: FilterStyle;
  itemSearchInputClass:
    | React.ComponentClass<ItemSearchInputParentProps<TItem>>
    | React.FunctionComponent<ItemSearchInputParentProps<TItem>>;
  itemSearchInputExtraProps?: object;
  onChangeOperator: (value: Operator) => void;
  onChangeValue: (items: Array<TItem>) => void;
  onEnter?: () => void;
  onRemove?: () => void;
  operators: Array<Operator>;
  selectedOperator: Operator;
  value: Array<TItem>;
  dropdownToggleText: string;
}

function ItemSearchInputFilterForm<TItem extends Item>(
  props: ItemSearchInputFilterFormProps<TItem>
): JSX.Element {
  const skeletonProps = pickDefined(props, ...BASE_FILTER_FORM_PROP_NAMES);
  const operatorDropdown = (
    <OperatorDropdown
      buttonSize={props.buttonSize}
      onChangeOperator={props.onChangeOperator}
      operators={props.operators}
      selectedOperator={props.selectedOperator}
    />
  );
  const itemSearchInput = (
    <props.itemSearchInputClass
      autoFocus={false}
      onChange={props.onChangeValue}
      onKeyDown={Keyboard.callOnEnter(props.onEnter)}
      selectedItems={props.value}
      fetchParams={{context: SearchContext.LISTING}}
      {...(props.itemSearchInputExtraProps || {})}
    />
  );
  if (props.filterStyle === FilterStyle.PRIMARY) {
    return (
      <PrimaryFilterFormSkeleton
        {...skeletonProps}
        buttonSize={props.buttonSize}
        content={<FilterFormSkeletonNodeLayout nodeGrid={[[operatorDropdown], [itemSearchInput]]} />}
        dropdownToggleText={props.dropdownToggleText}
      />
    );
  } else {
    if (!props.onRemove) {
      throw new Error('Expected props.onRemove to be present when filterStyle is SECONDARY');
    }
    return (
      <SecondaryFilterFormSkeleton
        {...skeletonProps}
        nodeGrid={[[operatorDropdown], [itemSearchInput]]}
        onChangeFilterKey={props.onChangeFilterKey}
        selectedFilterKey={props.selectedFilterKey}
        onRemove={props.onRemove}
      />
    );
  }
}

export default ItemSearchInputFilterForm;
